import { asArray, asString } from 'ol/color'
import { transformExtent } from 'ol/proj'
import Circle from 'ol/style/Circle'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import Style from 'ol/style/Style'
import Text from 'ol/style/Text'

import { charmap } from './ar5/charmap'
import { d3PointFromId } from './helperFunctions'
import { defs, highlightId } from './map/d3Renderer'
import { vectorLayer } from './map/layers'
import { map } from './map/tools'
import {
  CURRENT_GEOTYPE, debug, layerStyle, locallyAr5, setLayerStyle
} from './storage'

export const redStyle = new Style({
  stroke: new Stroke({
    color: 'red',
    width: 3
  }),
  fill: new Fill({
    color: 'rgba(0, 0, 255, 0.1)'
  })
});

const bluePointStyle = new Style({
  image: new Circle({
    radius: 10,
    fill: new Fill({
      color: 'blue'
    })
  })
});

export const bluePolygonStyle = new Style({
  fill: new Fill({
    color: 'rgba(255, 255, 255, 0.5)'
  }),
  stroke: new Stroke({
    color: 'blue',
    lineDash: [4],
    width: 2
  }),
  image: new Circle({
    radius: 7,
    fill: new Fill({
      color: '#ffcc33'
    })
  })
});

export const blueLineStyle = new Style({
  fill: new Fill({
    color: 'rgba(255, 255, 255, 0.5)'
  }),
  stroke: new Stroke({
    color: 'blue',
    lineDash: [4],
    width: 6
  }),
  image: new Circle({
    radius: 7,
    fill: new Fill({
      color: '#ffcc33'
    })
  })
});

export const redSelectedStylePolygon = new Style({
  stroke: new Stroke({ color: 'red', width: 4 }),
  fill: new Fill({ color: 'rgba(0, 0, 255, 0.1)' })
});

export const redSelectedStyleLine = new Style({
  stroke: new Stroke({
    color: 'rgba(255, 0, 0, 0.4)',
    width: 10
  }),
  fill: new Fill({
    color: 'rgba(0, 0, 255, 0.1)'
  })
});

export const redSelectedStylePoint = new Style({
  image: new Circle({
    radius: 10,
    fill: new Fill({
      color: 'red'
    })
  })
});

export const invisibleStylePoint = new Style({
  image: new Circle({
    radius: 0,
    fill: new Fill({
      color: 'red'
    })
  })
});

export const invisibleStyle = new Style({
  stroke: new Stroke({
    color: 'rgba(255, 255, 255, 0.1)',
    width: 8
  }),
  image: new Circle({
    radius: 8,
    fill: new Fill({
      color: 'rgba(255, 255, 255, 0.1)'
    })
  })
});

export function loadStyle(currentLayerId) {
  console.log(currentLayerId);
  if (debug < 16 && locallyAr5) {
    setTimeout(() => {
      const mapProjection = map.getView().getProjection().getCode();
      map.getView().fit(transformExtent(
        [1241099, 7904362, 1241262, 7904483], 'EPSG:25832',
        // [702927, 7224679, 703127, 7224879], 'EPSG:25832', // snap fails
        // [702300, 7224060, 702500, 7224260], 'EPSG:25832', // snap fails 2
        // [568694, 7033959, 568794, 7034059], 'EPSG:25832', // Trondheim
        // [569704, 7783047, 569704, 7783047], 'EPSG:25835',
      mapProjection));
      // document.getElementById('flybilder').click();
      // map.getView().setZoom(12);
    }, 500);
  }
  getOlStyle(currentLayerId, CURRENT_GEOTYPE).then(style => {
    // if (CURRENT_GEOTYPE === 'surface') { // use only OpenLayers, not D3
      vectorLayer.setStyle(style.ol); // load layer style for main map
    // } else { // Use both OpenLayers (for highlights) and D3
      // Used by d3, can be substituted by openlayers:
      useLayerStyle(style.json);
    // }
  });

  function useLayerStyle(data) {
    setLayerStyle(data);
    if (CURRENT_GEOTYPE === 'surface') {
      // Setup styles in OpenLayers and D3
      // Strokes are always handeld by OL, normal fill by OL, hatch,dots by D3
      const s1 = layerStyle.styles[0];
      const hatches = (s1.fillType === 'hatch');
      const dots = (s1.fillType === 'dots');
      const opacity = (hatches || dots) ? '0)' : '1)'
      const fillColor = 'rgba(255, 255, 255, ' + opacity;

      const olstyle =  new Style({
        fill: new Fill({
          color: fillColor
        }),
        stroke: new Stroke({
          color: s1.strokeColor,
          width: s1.strokeWidth,
          lineDash: [10, 3]
        })
      });

      // If special fill then setup D3
      if (hatches) {
        layerStyle.styles.forEach(style => {
          const pattern = defs.append('pattern');
          pattern
          .attr('id', style.styleName)
          .attr('width', style.hatchWidth)
          .attr('height', style.hatchWidth)
          .attr('patternUnits', 'userSpaceOnUse')
          .attr('patternTransform', 'rotate(-45)')
          .append('rect')
          .attr('width', 1.5)
          .attr('height', style.hatchWidth)
          .attr('transform', 'translate(0,0)')
          .attr('fill', style.fillColor);
        });
      } else if (dots) {
        layerStyle.styles.forEach(style => {
          const fc = style.fillColor;
          const pattern = defs.append('pattern');
          pattern
          .attr('id', style.styleName)
          .attr('x', style.x)
          .attr('y', style.y)
          .attr('width', style.width)
          .attr('height', style.height)
          .attr('patternUnits', 'userSpaceOnUse')
          .append('circle')
          .attr('cx', style.cx)
          .attr('cy', style.cy)
          .attr('r', style.radius)
          .attr(
            'style',
            'stroke-width:1; stroke:' + fc + '; fill-opacity:.5; opacity:.5; fill:' + fc
          );
        });
      }
    }
  }
}

const getArealText = (feature, resolution) => {
  let text = '';
  const areal = Math.abs(feature.getGeometry().getArea()) / 1000; // dekar
  // No lable if resolution is more than 4 or tiny area
  if ( (areal >= 200 && resolution < 4) ||
    (areal >= 5 && areal < 200 && resolution < 2) ||
    (areal >= 1 && areal < 5 && resolution < 1) ||
    (areal >= 0.1 && areal <= 1 && resolution < 0.1) ) {
    // text = areal.toFixed(1); // round to first decimal
    text = areal.toLocaleString('no-NO', {
      minimumFractionDigits: 1, maximumFractionDigits: 1
    }); // round to first decimal and use Norwegian format
  }
  return text;
};

const getSymbols = (feature, resolution) => {
  let codedSymbols = '';
  const areal = Math.abs(feature.getGeometry().getArea()) / 1000; // dekar
  if ( (areal >= 200 && resolution < 4) ||
    (areal >= 5 && areal < 200 && resolution < 2) ||
    (areal >= 1 && areal < 5 && resolution < 1) ||
    (areal >= 0.1 && areal <= 1 && resolution < 0.1) ) {
    codedSymbols = '\n';
    ['arealtype', 'treslag', 'skogbonitet', 'grunnforhold'].forEach(item => {
      // console.log(
      //   item, '-->', feature.get(item), '-->', charmap[item][feature.get(item)]
      // );
      codedSymbols += charmap[item][feature.get(item)];
    });
  }
  return codedSymbols;
};

const arealStyle = (feature, resolution) => {
  return new Style({
    text: new Text({
      fill: new Fill({ color: 'yellow' }),
      stroke: new Stroke({ color: 'gray', width: 3 }),
      text: getArealText(feature, resolution), // + ' dekar',
      font: 'normal italic 13px helvetica',
      textBaseline: 'ideographic',
      offsetY: -12
    }),
    zIndex: 4
  });
};

const symbolStyle = (feature, resolution) => {
  return new Style({
    text: new Text({
      fill: new Fill({ color: 'yellow' }),
      stroke: new Stroke({ color: 'gray', width: 3 }),
      // text: '\nw0ij', // a newline is needed to avoid clipped symbols
      text: getSymbols(feature, resolution),
      font: '700 24px ARsymbols',
      textBaseline: 'ideographic',
      // textBaseline: 'hanging',
      offsetY: 12,
      // padding: [10, 10, 10, 10],
      // overflow: true,
      // textAlign: 'left',
      // backgroundFill: new Fill({ color: 'blue' }),
      // backgroundStroke: new Stroke({ color: 'red', width: 3 }),
      // scale: 2
    }),
    zIndex: 4
  });
};

export const styleFunctionFromOpacity = {
  json: {},
  get: (opacity) => {
    const jsonStyle = styleFunctionFromOpacity.json;
    if (!jsonStyle.styles) return;
    const fillType = jsonStyle.styles[0].fillType;
    const hatchDots = (fillType === 'hatch' || fillType === 'dots');
    const fillOpacity = hatchDots ? 0 : 1;
    const fillColor = 'rgba(255, 255, 255, ' + fillOpacity + ')';

    const customFill = new Fill({
      color: 'rgba(255, 255, 255, 0.5)'
    });
    const customStroke = new Stroke({
      color: jsonStyle.styles[0].strokeColor,
      width: jsonStyle.styles[0].strokeWidth,
      lineDash: [10, 3]
    });
    const generalStyle = new Style({
      fill: customFill,
      stroke: customStroke
    });

    const styleAttribute = jsonStyle.styleAttribute;
    const dependentOnOpacityFunction = (f, r) => {
      // Show/style only polygons in case ar5web sends linestrings too:
      if (f.getGeometry().getType().indexOf('Polygon') < 0) return;
      const p = f.getProperties();
      const isVisible = (p.slette_status_kode === 0); // not null and not 1
      // Do not remove "+", it converts to number in case it is a string:
      const styleAttributeValue = p[styleAttribute] ? +p[styleAttribute] : null;
      let styleIndex = styleAttribute ? (styleAttributeValue - 1) % 2 : 0;
      // Find the correct style for this feature:
      const sl = jsonStyle.styles.length;
      for (let i = 0; i < sl; i++) {
        if (jsonStyle.styles[i].value === styleAttributeValue) styleIndex = i;
      }
      const chosenStyle = jsonStyle.styles[styleIndex];
      if (isVisible && (!styleAttribute || styleAttributeValue)) {
        customFill.setColor(getPattern(chosenStyle));
        customStroke.setColor(chosenStyle.strokeColor);
        customStroke.setWidth(chosenStyle.strokeWidth);
        customStroke.setLineDash();
      } else {
        const qualityCheckGhostStyle = p.saksbehandler !== undefined;
        const o = qualityCheckGhostStyle ? 0.5 : 0; // slettet flater helt gjennomsiktige
        customFill.setColor(`rgba(255, 255, 255, ${o})`);
        customStroke.setColor(`rgba(0, 0, 0, ${o})`);
        customStroke.setLineDash([10, 3]);
      }
      const areal = locallyAr5 ? arealStyle(f, r) : new Style();
      const symbol = locallyAr5 ? symbolStyle(f, r) : new Style();
      if (locallyAr5) {
        const [bc, z] = boundaryColour(jsonStyle.orthophoto, p);
        const stroke = bc || (chosenStyle ? chosenStyle.strokeColor : 'yellow');
        customFill.setColor(getPattern(chosenStyle, opacity));
        customStroke.setColor(stroke);
        customStroke.setLineDash();
        generalStyle.setZIndex(z);
      }
      return [generalStyle, areal, symbol];
    };
    return dependentOnOpacityFunction;
  }
};

export function getOlStyle(currentLayerId, geoType) {
  // do not cache styles, they must be independent
  // if (olStyles.hasOwnProperty(currentLayerId)) {
  //   console.log('style for ', currentLayerId, 'has been already created');
  //   return olStyles[currentLayerId];
  // }
  // const promise = $.getJSON('styles/' + currentLayerId + '.json').then(jsonStyle => {
  const promise = import(`../styles/${currentLayerId}.json`).then(jsonStyle => {
    // useLayerStyle(jsonStyle); // Used by d3, can be substituted by openlayers:
    let olStyle = redStyle;
    if (geoType === 'point') {
      // we use D3 to show markers
      // olStyle = invisibleStyle;
    }
    if (geoType === 'line') {
      // we have to use D3 to show markers along lines
      olStyle = invisibleStyle;
    }
    if (geoType === 'surface') {
      // The possibility to choose opacity will be usefull with sliders:
      styleFunctionFromOpacity.json = jsonStyle;
      const ar5_opacity = locallyAr5 ? (rangePG.value / 100) : 0;
      olStyle = styleFunctionFromOpacity.get(ar5_opacity);
    } // if surface
    return { ol: olStyle, json: jsonStyle };
  });
  // olStyles[currentLayerId] = promise;
  return promise;
}

function getPattern(s, opacity) {
  const fillType = s.fillType;

  const noRandom = (s?.fillColor !== 'random') ? s.fillColor : 'transparent';
  const rgba = asArray(s ? noRandom : 'rgba(0, 0, 0, 1)');
  if (opacity || opacity === 0) rgba[3] = opacity;

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  // const pixelRatio = ol.has.DEVICE_PIXEL_RATIO; // does not work on mac FF
  const pixelRatio = 1;

  if (fillType === 'fill') return rgba;
  if (fillType === 'hatch') {
    const radians = (s.hatchAngle ?? 45) * (Math.PI / 180);
    const w = (s.hatchGap || s.hatchWidth) * 0.8;
    canvas.width = 2 * w * pixelRatio;
    canvas.height = 2 * w * pixelRatio;
    const c = w * Math.tan(-radians);
    // transparent background
    context.fillStyle = 'transparent';
    context.strokeStyle = asString(rgba);
    context.lineWidth = (s.hatchWidthOl || s.hatchWidth || s.strokeWidth) * 1.2;
    context.lineCap = 'square';
    context.beginPath(); // Reset the current path
    context.moveTo(0, 2 * w - c);
    context.lineTo(2 * w, 2 * w + c);
    context.moveTo(0, w - c);
    context.lineTo(2 * w, w + c);
    context.moveTo(0, -c);
    context.lineTo(2 * w, c);
    context.stroke(); // Make the line visible
  } else if (fillType === 'dots') {
    // pattern definition:
    canvas.width = 8 * pixelRatio;
    canvas.height = 8 * pixelRatio;
    // transparent background
    context.fillStyle = 'transparent';
    context.fillRect(0, 0, canvas.width, canvas.height);
    // outer circle
    context.fillStyle = s.strokeColor;
    context.beginPath();
    context.arc(4 * pixelRatio, 4 * pixelRatio, 3 * pixelRatio, 0, 2 * Math.PI);
    context.fill();
    // inner circle
    context.fillStyle = rgba;
    context.beginPath();
    context.arc(4 * pixelRatio, 4 * pixelRatio, 1.5 * pixelRatio, 0, 2 * Math.PI);
    context.fill();
  }

  return context.createPattern(canvas, 'repeat');
}

export function setRedStyle(s, id) {
  switch (CURRENT_GEOTYPE) {
    case 'point':
      const selectedPointD3 = d3PointFromId(id);
      // selectedPointD3.attr('originalStyle', selectedPointD3.attr('style'));
      selectedPointD3.style('fill', 'red');
      // selectedPointD3.datum().properties.selected = true;
      break;
    case 'line':
      if (s) s.setStyle(redSelectedStyleLine);
      break;
    // case 'surface':
    //   break;
    default:
      // if (s) s.setStyle(redStyle); // it covers symbols
      if (s?.get('arealtype') === '60') highlightId(id); // only needed for myr in chrome
  }
}

export const boundaryColour = (boundaries, properties) => {
  let colour = null; // default colour will be used
  let z = 0;
  boundaries.some((b, i) => {
    let allConstraintsFulfilled = true;
    for (let key of Object.keys(b.constraints)) {
      const cv = b.constraints[key]; // constraint values
      const pv = properties[key]; // property value
      if (debug < 10) console.log(key, pv, cv);
      if (cv.length) { // array of values
        let singleConstraintFulfilled = false;
        for (let ai = 0; ai < cv.length; ai++) {
          if (pv && pv.indexOf(cv[ai]) === 0) { // constraint fulfilled
            singleConstraintFulfilled = true;
            break; // for-loop on single constraint array
          }
        }
        if (!singleConstraintFulfilled) {
          allConstraintsFulfilled = false;
          break; // for-loop on constraints
        }
      } else if (cv === true) { // property just has not to be empty
        if (pv === '' || !pv) {
          allConstraintsFulfilled = false;
          break; // for-loop on constraints
        }
      } else {
        if (cv !== pv) {
          allConstraintsFulfilled = false;
          break; // for-loop on constraints
        }
      }
    }
    if (allConstraintsFulfilled) {
      colour = b.strokeColor;
      z = b.zIndex;
      return true; // break loop on boundaries:
      // the first boundary colour satisfying all constraints excludes others.
    } else return false;
  });
  return [colour, z];
};
